import React, { useState } from 'react';
import { Bell, BellRing, CheckCheck, Trash2, BellOff, ExternalLink, Building2 } from 'lucide-react';
import { Lead } from '../types';
import { resolveLeadsForNotification } from '../lib/notificationLeadResolver';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  time: string; // ISO timestamp
  read: boolean;
  leadId?: string;
  leadIds?: string[];
}

interface NotificationBellProps {
  notifications: AppNotification[];
  leads?: Lead[];
  onMarkAllRead: () => void;
  onMarkRead: (id: string) => void;
  onClearAll: () => void;
  onSelectLead?: (leadId: string) => void;
}

const timeAgo = (iso: string): string => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function NotificationBell({
  notifications,
  leads = [],
  onMarkAllRead,
  onMarkRead,
  onClearAll,
  onSelectLead,
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    supported ? Notification.permission : 'unsupported'
  );

  const unread = notifications.filter(n => !n.read).length;

  const handleEnablePush = async () => {
    if (!supported) return;
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm === 'granted') {
      new Notification('Armellini CRM notifications enabled', {
        body: "You'll be alerted about email replies and follow-ups due.",
      });
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition cursor-pointer"
        title="Notifications"
      >
        {unread > 0 ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-950">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-[340px] md:w-[380px] origin-top-right rounded-xl bg-white dark:bg-slate-950 shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-slate-800 z-50 border border-slate-100 dark:border-slate-800">

            <div className="p-3.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 rounded-t-xl flex justify-between items-center">
              <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Notifications</p>
              {notifications.length > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={onMarkAllRead}
                    className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 px-2 py-1 rounded flex items-center gap-1 transition cursor-pointer"
                  >
                    <CheckCheck className="w-3 h-3" /> Mark all read
                  </button>
                  <button
                    onClick={onClearAll}
                    className="text-[10px] font-bold text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 px-2 py-1 rounded flex items-center gap-1 transition cursor-pointer"
                  >
                    <Trash2 className="w-3 h-3" /> Clear
                  </button>
                </div>
              )}
            </div>

            {/* Push opt-in banner */}
            {permission === 'default' && (
              <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-blue-50/60 dark:bg-blue-950/20 flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">
                  Get desktop alerts for email replies and follow-ups, even when the tab is in the background.
                </p>
                <button
                  onClick={handleEnablePush}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg transition cursor-pointer"
                >
                  Enable
                </button>
              </div>
            )}
            {permission === 'denied' && (
              <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 flex items-center gap-2">
                <BellOff className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
                  Desktop alerts are blocked. Allow notifications for this site in your browser settings to enable them.
                </p>
              </div>
            )}

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-8 h-8 text-slate-200 dark:text-slate-700 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No notifications yet.</p>
                  <p className="text-[10px] text-slate-400 mt-1">Email replies and due follow-ups will show up here.</p>
                </div>
              ) : (
              notifications.map((n) => {
                const resolvedLeads = resolveLeadsForNotification(n, leads);
                const primaryLead = resolvedLeads[0];
                const hasRecord = Boolean(primaryLead);

                return (
                  <div
                    key={n.id}
                    onClick={() => {
                      onMarkRead(n.id);
                      if (primaryLead && onSelectLead) {
                        onSelectLead(primaryLead.id);
                        setIsOpen(false);
                      }
                    }}
                    className={`w-full text-left p-3.5 border-b border-slate-50 dark:border-slate-900 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition ${
                      hasRecord ? 'cursor-pointer hover:border-l-2 hover:border-l-blue-600' : ''
                    } ${n.read ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {!n.read && <span className="mt-1.5 w-2 h-2 bg-blue-600 rounded-full shrink-0" />}
                      <div className={`min-w-0 flex-1 ${n.read ? 'pl-[18px]' : ''}`}>
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{n.title}</p>
                          {hasRecord && (
                            <span className="shrink-0 text-[10px] text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-0.5">
                              <ExternalLink className="w-2.5 h-2.5" /> Open
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">{n.body}</p>

                        {/* If multiple leads are referenced (e.g. 4 follow-ups due), show direct clickable chips */}
                        {resolvedLeads.length > 1 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {resolvedLeads.map((rl) => (
                              <button
                                key={rl.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMarkRead(n.id);
                                  if (onSelectLead) {
                                    onSelectLead(rl.id);
                                    setIsOpen(false);
                                  }
                                }}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 text-[10px] font-bold rounded border border-blue-200 dark:border-blue-800 transition cursor-pointer"
                                title={`Open ${rl.company || rl.name}`}
                              >
                                <Building2 className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate max-w-[120px]">{rl.company || rl.name}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {resolvedLeads.length === 1 && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                            <Building2 className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate">{primaryLead.company || primaryLead.name}</span>
                          </div>
                        )}

                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 font-mono">{timeAgo(n.time)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
